import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { selectPlans } from '@mm/state/selectors/facebookPageSelector';
import ShopifyController from '@mm/state/controllers/ShopifyController';
import {
  selectIsFreeShopify,
  selectShopifyShop,
} from '@mm/state/selectors/viewSelectors';
import { BillingPlanCardProps } from './use-billing-plans';
import { useFreeTrialUpgradeModal } from '../../../../../common/modals/FreeTrialUpgradeModal/use-free-trial-upgrade-modal';
import { Plan } from '@mm/state/interfaces/subscription';
import { SuperCapturePixelInfo } from '../../../../../modules/Shopify/Billing/SuperCapturePixelInfo';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import pluralize from 'pluralize';

const mappings: Record<string, Partial<BillingPlanCardProps>> = {
  shopify_intro: {
    buttonName: 'Get Started',
    secondaryPoints: [
      'X-Ray Pixel',
      'Advanced CRM Integrations',
      'Suppression lists',
    ],
    costs: ['$0.25 per contact captured'],
  },
  shopify_starter: {
    buttonName: 'Get Started',
    secondaryPoints: [
      'X-Ray Pixel',
      'Advanced CRM Integrations',
      'Suppression lists',
    ],
    costs: ['$0.23 per contact captured'],
  },
  shopify_pro: {
    buttonName: 'Get Started',
    points: [],
    secondaryPoints: [
      'X-Ray Pixel',
      'Advanced CRM Integrations',
      'Suppression lists',
    ],
    costs: ['$0.18 per contact captured'],
    tag: 'Most popular',
    tagBgColor: '#63A4C9',
  },
  shopify_advanced: {
    buttonName: 'Use All Features',
    points: ['Everything in Pro and:'],
    secondaryPoints: [
      'Customer success manager',
      <SuperCapturePixelInfo key="supercapture" />,
    ],
    costs: ['$0.13 per contact captured'],
    cardBgColor: '#F7ECFF',
  },
};

export const SHOPIFY_PLAN_CODES = [
  'shopify_intro',
  'shopify_starter',
  'shopify_pro',
  'shopify_advanced',
];

export const useShopifyBillingPlans = ({
  withFree,
}: {
  withFree?: boolean;
} = {}) => {
  const plansSource = useSelector(selectPlans);

  const modalActions = useModalActions();

  const getPlan = (planCode: string) =>
    plansSource.find(v => v.planCode === planCode) as Plan;

  const shop = useSelector(selectShopifyShop);
  const isFree = useSelector(selectIsFreeShopify);

  const [isPurchasing, setIsPurchasing] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const leadCount =
    typeof shop?.contactsLimits === 'number' ? shop.contactsLimits : 500;

  const handlePurchase = async (planId: number) => {
    if (planId && shop?.id) {
      setIsPurchasing(true);
      const res = await ShopifyController.subscribeShopifyPlan(shop.id, planId);

      if (res?.confirmationUrl) {
        window.location.href = res.confirmationUrl;
      }

      setIsPurchasing(false);
    }
  };

  const handleCancel = async () => {
    if (shop?.id) {
      modalActions.openGeneralConfirmModal({
        headerText: 'Cancel plan',
        messageText: 'Are you sure you want to cancel current plan?',
        okButtonStyling: { red: true },
        onCloseHandler: async ok => {
          if (ok) {
            try {
              setIsCancelling(true);
              const res = await ShopifyController.cancelShopifyPlan(shop.id);

              if (res?.success) {
                window.location.reload();
              }
            } finally {
              setIsCancelling(false);
            }
          }
        },
        okButtonText: 'Yes, Cancel the Plan',
        cancelButtonText: 'No',
      });
    }
  };

  const { currentSubscription } = useFreeTrialUpgradeModal();

  const isDowngrade = (code: string) => {
    switch (currentSubscription?.planCode) {
      case code: {
        return false;
      }
      case 'shopify_starter': {
        return ['shopify_intro'].includes(code);
      }
      case 'shopify_pro': {
        return ['shopify_intro', 'shopify_starter'].includes(code);
      }
      case 'shopify_advanced': {
        return true;
      }
      default:
        return false;
    }
  };

  const isCurrent = (code: string) => {
    if (currentSubscription?.state !== 'active') {
      return false;
    }

    return currentSubscription?.planCode === code;
  };

  const buttonName = (code: string) =>
    isDowngrade(code)
      ? 'Downgrade'
      : isCurrent(code)
      ? 'Current plan'
      : mappings[code]?.buttonName || '';

  const shopifyPlans = [
    ...(withFree
      ? [
          {
            title: 'Free',
            price: '$0',
            pricePeriod: '',
            buttonName: isFree ? 'Current plan' : 'Downgrade',
            onClick: () => {
              if (!isFree) {
                handleCancel();
              }
            },
            costs: ['Free'],
            secondaryPoints: [
              `${leadCount} Free ${pluralize('lead', leadCount)}`,
              '14 days free',
            ],
            hide: false,
            planCode: 'shopify_free', // FE only plan for styling
            downgrade: !isFree,
            currentTag: isFree,
          },
        ]
      : []),
    ...SHOPIFY_PLAN_CODES.map(planCode => getPlan(planCode))
      .filter(Boolean)
      .map(plan => ({
        title: plan?.shortName || '',
        price: `$${(plan?.unitAmountInCents || 0) / 100}`,
        pricePeriod: plan?.billingPeriod === 'month' ? '/mo' : 'year',
        buttonName: buttonName(plan.planCode),
        planSubtypes: mappings[plan.planCode]?.planSubtypes || [],
        onClick: () => handlePurchase(plan.id),
        points: mappings[plan.planCode]?.points || [],
        secondaryPoints: mappings[plan.planCode]?.secondaryPoints || [],
        costs: mappings[plan.planCode]?.costs || [],
        tag: mappings[plan.planCode]?.tag,
        tagBgColor: mappings[plan.planCode]?.tagBgColor,
        cardBgColor: mappings[plan.planCode]?.cardBgColor,
        currentTag: isCurrent(plan.planCode),
        planCode: plan.planCode,
      })),
  ];

  return { plans: shopifyPlans, isPurchasing, handleCancel, isCancelling };
};
